import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, FileText, Calculator, Building2, Trash2, RefreshCw, CheckCircle, Clock, Download, ArrowRight, DollarSign, Check, Receipt, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
];

const UNITS = [
  { value: 'mq', label: 'mq' },
  { value: 'ml', label: 'ml' },
  { value: 'ore', label: 'ore' },
  { value: 'pz', label: 'pz' },
  { value: 'kg', label: 'kg' },
];

const CATEGORIES = [
  { value: 'lavorazione', label: 'Lavorazione' },
  { value: 'fornitura', label: 'Fornitura' },
  { value: 'manodopera', label: 'Manodopera' },
  { value: 'noleggio', label: 'Noleggio' },
];

const PRICE_TYPES = [
  { value: 'standard', label: 'Voce Standard' },
  { value: 'percentage_increase', label: 'Aumento %' },
  { value: 'percentage_discount', label: 'Sconto %' },
];

export default function SALPage() {
  const { isRole } = useAuth();
  const [sites, setSites] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [salEntries, setSalEntries] = useState([]);
  const [salReports, setSalReports] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  
  const [selectedSite, setSelectedSite] = useState('__all__');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterMonth, setFilterMonth] = useState(0); // 0 = tutti i mesi
  const [salPage, setSalPage] = useState(1);
  const SAL_PAGE_SIZE = 25;
  const [priceSearch, setPriceSearch] = useState('');
  // Paginazione server-side prezziario
  const [pricePage, setPricePage] = useState(1);
  const [priceTotalPages, setPriceTotalPages] = useState(1);
  const [priceTotal, setPriceTotal] = useState(0);
  const [priceCategory, setPriceCategory] = useState('__all__');
  const [priceLoading, setPriceLoading] = useState(false);
  const PRICE_PAGE_SIZE = 50;
  // Lista completa per dropdown SAL entry
  const [allPriceItems, setAllPriceItems] = useState([]);
  
  // Dialogs
  const [showPriceCreate, setShowPriceCreate] = useState(false);
  const [showEntryCreate, setShowEntryCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showReportDetail, setShowReportDetail] = useState(null);
  
  // Forms
  const [priceForm, setPriceForm] = useState({
    code: '',
    description: '',
    category: 'lavorazione',
    subcategory: '',
    unit: 'mq',
    price_type: 'standard',
    percentage_value: '',
    price_supply: '',
    price_work: '',
    notes: ''
  });
  
  const [entryForm, setEntryForm] = useState({
    site_id: '',
    price_item_id: '',
    date: new Date().toISOString().split('T')[0],
    quantity: '',
    description: '',
    notes: '',
    // Nuovi campi facoltativi
    numero_pdl: '',
    work_order: '',
    area: '',
    item: '',
    // Voci accessorie
    accessory_items: []
  });
  
  const [generateForm, setGenerateForm] = useState({
    site_id: '',
    year: currentYear,
    month: currentMonth,
    notes: ''
  });

  const load = async () => {
    try {
      const [sitesRes, entriesRes, reportsRes, finRes, allPricesRes] = await Promise.all([
        api.get('/sites'),
        api.get('/sal/entries', { params: { site_id: selectedSite !== '__all__' ? selectedSite : undefined, year: filterYear, month: filterMonth || undefined } }),
        api.get('/sal/reports', { params: { site_id: selectedSite !== '__all__' ? selectedSite : undefined, year: filterYear } }),
        api.get('/sal/financial-summary', { params: { site_id: selectedSite !== '__all__' ? selectedSite : undefined, year: filterYear } }).catch(() => ({ data: null })),
        allPriceItems.length === 0 ? api.get('/price-lists') : Promise.resolve({ data: allPriceItems }),
      ]);
      setSites(sitesRes.data);
      setSalEntries(entriesRes.data);
      setSalReports(reportsRes.data);
      setFinancialSummary(finRes.data);
      if (Array.isArray(allPricesRes.data)) setAllPriceItems(allPricesRes.data);
      setSelectedEntries([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch prezziario con paginazione server-side
  const loadPriceItems = async (pg, search, cat) => {
    setPriceLoading(true);
    try {
      const params = { page: pg, per_page: PRICE_PAGE_SIZE };
      if (search?.trim()) params.search = search.trim();
      if (cat && cat !== '__all__') params.category = cat;
      const res = await api.get('/price-lists', { params });
      setPriceItems(res.data.items);
      setPriceTotal(res.data.total);
      setPriceTotalPages(res.data.total_pages);
      setPricePage(res.data.page);
    } catch (err) {
      console.error(err);
    } finally {
      setPriceLoading(false);
    }
  };

  useEffect(() => { load(); setSalPage(1); }, [selectedSite, filterYear, filterMonth]);

  // Ricarica prezziario quando cambiano filtri/pagina
  useEffect(() => {
    const timer = setTimeout(() => {
      loadPriceItems(pricePage, priceSearch, priceCategory);
    }, priceSearch ? 350 : 0);
    return () => clearTimeout(timer);
  }, [pricePage, priceSearch, priceCategory]);

  // ─── CAMBIO STATO ────────────────────────────────────────
  const STATUS_FLOW = { inserito: 'approvato', approvato: 'fatturato', fatturato: 'incassato' };
  const STATUS_COLORS = {
    inserito: 'bg-slate-100 text-slate-700 border-slate-300',
    approvato: 'bg-green-50 text-green-700 border-green-300',
    fatturato: 'bg-blue-50 text-blue-700 border-blue-300',
    incassato: 'bg-purple-50 text-purple-700 border-purple-300'
  };
  const STATUS_ICONS = { inserito: Clock, approvato: Check, fatturato: Receipt, incassato: DollarSign };

  const changeEntryStatus = async (entryId, newStatus) => {
    try {
      await api.put(`/sal/entries/${entryId}/status`, { status: newStatus });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore transizione'); }
  };

  const bulkChangeStatus = async (newStatus) => {
    if (selectedEntries.length === 0) { alert('Seleziona almeno una voce'); return; }
    try {
      const res = await api.put('/sal/entries/bulk-status', { entry_ids: selectedEntries, status: newStatus });
      if (res.data.errors?.length) alert(`${res.data.updated} aggiornate, ${res.data.errors.length} errori`);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const changeReportStatus = async (reportId, newStatus) => {
    try {
      await api.put(`/sal/reports/${reportId}`, { status: newStatus });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore transizione report'); }
  };

  const handleCreatePrice = async () => {
    try {
      await api.post('/price-lists', {
        ...priceForm,
        price_supply: priceForm.price_type === 'standard' ? (parseFloat(priceForm.price_supply) || 0) : 0,
        price_work: priceForm.price_type === 'standard' ? (parseFloat(priceForm.price_work) || 0) : 0,
        percentage_value: priceForm.price_type !== 'standard' ? (parseFloat(priceForm.percentage_value) || 0) : 0
      });
      setShowPriceCreate(false);
      setPriceForm({ code: '', description: '', category: 'lavorazione', subcategory: '', unit: 'mq', price_type: 'standard', percentage_value: '', price_supply: '', price_work: '', notes: '' });
      loadPriceItems(pricePage, priceSearch, priceCategory);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleCreateEntry = async () => {
    try {
      await api.post('/sal/entries', {
        ...entryForm,
        quantity: parseFloat(entryForm.quantity) || 0,
        accessory_items: entryForm.accessory_items.map(acc => ({ price_item_id: acc.price_item_id }))
      });
      setShowEntryCreate(false);
      setEntryForm({ 
        site_id: '', 
        price_item_id: '', 
        date: new Date().toISOString().split('T')[0], 
        quantity: '', 
        description: '', 
        notes: '',
        numero_pdl: '',
        work_order: '',
        area: '',
        item: '',
        accessory_items: []
      });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await api.post('/sal/reports/generate', generateForm);
      setShowGenerate(false);
      setShowReportDetail(res.data);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Eliminare questa voce?')) return;
    try {
      await api.delete(`/sal/entries/${entryId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (!window.confirm('Eliminare questa voce prezziario?')) return;
    try {
      await api.delete(`/price-lists/${priceId}`);
      loadPriceItems(pricePage, priceSearch, priceCategory);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDownloadPdf = async (reportId, reportNumber) => {
    try {
      const response = await api.get(`/sal/reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SAL_${reportNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore durante il download del PDF');
      console.error(err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Selected price item for preview
  const selectedPriceItem = allPriceItems.find(p => p.id === entryForm.price_item_id);
  const baseTotal = selectedPriceItem && entryForm.quantity 
    ? (parseFloat(entryForm.quantity) || 0) * (selectedPriceItem.price_total || 0)
    : 0;
  
  // Calculate accessory adjustments
  const accessoryAdjustment = entryForm.accessory_items.reduce((sum, acc) => {
    const accItem = priceItems.find(p => p.id === acc.price_item_id);
    if (accItem && accItem.percentage_value) {
      return sum + (baseTotal * (accItem.percentage_value / 100));
    }
    return sum;
  }, 0);
  
  const previewTotal = baseTotal + accessoryAdjustment;
  
  // Get only percentage variation items for accessory selection
  const percentageItems = allPriceItems.filter(p => p.price_type === 'percentage_increase' || p.price_type === 'percentage_discount');
  
  // Get only standard items for main selection
  const standardItems = allPriceItems.filter(p => !p.price_type || p.price_type === 'standard');

  // Calculate totals for entries
  const entriesTotals = salEntries.reduce((acc, e) => {
    acc.supply += e.total_supply || 0;
    acc.work += e.total_work || 0;
    acc.amount += e.total_amount || 0;
    return acc;
  }, { supply: 0, work: 0, amount: 0 });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento SAL...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sal-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">SAL - Stato Avanzamento Lavori</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(filterMonth)} onValueChange={(v) => setFilterMonth(parseInt(v))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tutti i mesi</SelectItem>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-48" data-testid="filter-site">
              <Building2 size={14} className="mr-1" />
              <SelectValue placeholder="Tutti i cantieri" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i cantieri</SelectItem>
              {sites.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500 mb-0.5">Inserito</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(financialSummary?.inserito?.total || 0)}</p>
            <p className="text-xs text-slate-400">{financialSummary?.inserito?.count || 0} voci</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-green-600 mb-0.5">Approvato</p>
            <p className="text-lg font-bold text-green-700">{formatCurrency(financialSummary?.approvato?.total || 0)}</p>
            <p className="text-xs text-green-500">{financialSummary?.approvato?.count || 0} voci</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-blue-600 mb-0.5">Fatturato</p>
            <p className="text-lg font-bold text-blue-700">{formatCurrency(financialSummary?.fatturato?.total || 0)}</p>
            <p className="text-xs text-blue-500">{financialSummary?.fatturato?.count || 0} voci</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-purple-600 mb-0.5">Incassato</p>
            <p className="text-lg font-bold text-purple-700">{formatCurrency(financialSummary?.incassato?.total || 0)}</p>
            <p className="text-xs text-purple-500">{financialSummary?.incassato?.count || 0} voci</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-300 bg-emerald-50/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-emerald-600 mb-0.5">Totale Generale</p>
            <p className="text-lg font-bold text-emerald-700">{formatCurrency(financialSummary?.totale?.total || entriesTotals.amount)}</p>
            <p className="text-xs text-emerald-500">{financialSummary?.totale?.count || salEntries.length} voci</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="lavori">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="lavori">Lavori Eseguiti ({salEntries.length})</TabsTrigger>
          <TabsTrigger value="reports">Report SAL ({salReports.length})</TabsTrigger>
          <TabsTrigger value="prezziario" data-testid="tab-prezziario">Prezziario ({priceTotal})</TabsTrigger>
        </TabsList>

        {/* Tab Lavori Eseguiti */}
        <TabsContent value="lavori">
          <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              {selectedEntries.length > 0 && isRole('admin', 'qhse') && (
                <>
                  <Button size="sm" variant="outline" onClick={() => bulkChangeStatus('approvato')} className="text-green-700 border-green-300" data-testid="bulk-approve">
                    <Check size={14} className="mr-1" /> Approva ({selectedEntries.length})
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkChangeStatus('fatturato')} className="text-blue-700 border-blue-300" data-testid="bulk-invoice">
                    <Receipt size={14} className="mr-1" /> Fattura ({selectedEntries.length})
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkChangeStatus('incassato')} className="text-purple-700 border-purple-300" data-testid="bulk-collect">
                    <DollarSign size={14} className="mr-1" /> Incassa ({selectedEntries.length})
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {isRole('admin', 'qhse', 'capo_cantiere') && (
                <>
                  <Button variant="outline" onClick={() => setShowGenerate(true)} data-testid="generate-sal-btn">
                    <Calculator size={16} className="mr-1" /> Genera SAL
                  </Button>
                  <Button onClick={() => setShowEntryCreate(true)} data-testid="add-entry-btn">
                    <Plus size={16} className="mr-1" /> Aggiungi Lavoro
                  </Button>
                </>
              )}
            </div>
          </div>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isRole('admin', 'qhse') && <TableHead className="w-10"><input type="checkbox" className="rounded" onChange={(e) => { if (e.target.checked) { setSelectedEntries(salEntries.map(x => x.id)); } else { setSelectedEntries([]); } }} checked={selectedEntries.length === salEntries.length && salEntries.length > 0} /></TableHead>}
                    <TableHead>Data</TableHead>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>Codice</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="text-right">Qta</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    {isRole('admin', 'qhse') && <TableHead className="w-24">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        Nessun lavoro registrato per questo periodo
                      </TableCell>
                    </TableRow>
                  ) : salEntries.slice((salPage - 1) * SAL_PAGE_SIZE, salPage * SAL_PAGE_SIZE).map(entry => {
                    const entryStatus = entry.status || 'inserito';
                    const nextStatus = STATUS_FLOW[entryStatus];
                    const StatusIcon = STATUS_ICONS[entryStatus] || Clock;
                    return (
                      <TableRow key={entry.id} className="hover:bg-slate-50">
                        {isRole('admin', 'qhse') && (
                          <TableCell><input type="checkbox" className="rounded" checked={selectedEntries.includes(entry.id)} onChange={() => toggleEntrySelection(entry.id)} /></TableCell>
                        )}
                        <TableCell className="text-sm">{entry.date}</TableCell>
                        <TableCell className="text-sm font-medium">{entry.site_name}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-700 font-mono text-xs">{entry.price_code}</Badge></TableCell>
                        <TableCell className="text-sm min-w-[200px]">{entry.price_description || entry.description}</TableCell>
                        <TableCell className="text-right text-sm">{entry.quantity} {entry.unit}</TableCell>
                        <TableCell className="text-right text-sm font-bold text-green-700">{formatCurrency(entry.total_amount)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${STATUS_COLORS[entryStatus]}`} data-testid={`status-${entry.id}`}>
                            <StatusIcon size={12} className="mr-1" />{entryStatus}
                          </Badge>
                        </TableCell>
                        {isRole('admin', 'qhse') && (
                          <TableCell>
                            <div className="flex gap-1">
                              {nextStatus && (
                                <Button variant="ghost" size="sm" onClick={() => changeEntryStatus(entry.id, nextStatus)} className="p-1 h-auto text-green-600" title={`Passa a ${nextStatus}`} data-testid={`advance-${entry.id}`}>
                                  <ArrowRight size={14} />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteEntry(entry.id)} className="text-red-600 hover:text-red-800 p-1 h-auto">
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {salEntries.length > 0 && (
                    <TableRow className="bg-slate-100 font-bold">
                      <TableCell colSpan={isRole('admin', 'qhse') ? 6 : 5}>TOTALE PERIODO</TableCell>
                      <TableCell className="text-right text-green-700">{formatCurrency(entriesTotals.amount)}</TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {salEntries.length > SAL_PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 px-1">
              <p className="text-xs text-slate-500">
                {(salPage - 1) * SAL_PAGE_SIZE + 1}-{Math.min(salPage * SAL_PAGE_SIZE, salEntries.length)} di {salEntries.length} voci
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={salPage <= 1} onClick={() => setSalPage(p => p - 1)} data-testid="sal-prev-page">
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-xs text-slate-600 px-2">Pag. {salPage}/{Math.ceil(salEntries.length / SAL_PAGE_SIZE)}</span>
                <Button variant="outline" size="sm" disabled={salPage >= Math.ceil(salEntries.length / SAL_PAGE_SIZE)} onClick={() => setSalPage(p => p + 1)} data-testid="sal-next-page">
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Tab Report SAL */}
        <TabsContent value="reports">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N. Report</TableHead>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Periodo</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Voci</TableHead>
                    <TableHead className="w-20">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salReports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        Nessun report SAL generato
                      </TableCell>
                    </TableRow>
                  ) : salReports.map(report => {
                    const repStatus = report.status || 'inserito';
                    const repNextStatus = STATUS_FLOW[repStatus];
                    const RepStatusIcon = STATUS_ICONS[repStatus] || Clock;
                    return (
                    <TableRow 
                      key={report.id} 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setShowReportDetail(report)}
                    >
                      <TableCell className="font-mono text-sm">{report.report_number}</TableCell>
                      <TableCell className="font-medium">{report.site_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{report.client}</TableCell>
                      <TableCell className="text-sm">{MONTHS.find(m => m.value === report.month)?.label} {report.year}</TableCell>
                      <TableCell className="text-right font-bold text-green-700">{formatCurrency(report.total_amount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${STATUS_COLORS[repStatus]}`}>
                          <RepStatusIcon size={12} className="mr-1" />{repStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-500">{report.entry_count}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {repNextStatus && isRole('admin', 'qhse') && (
                            <Button variant="ghost" size="sm" onClick={() => changeReportStatus(report.id, repNextStatus)} className="p-1 h-auto text-green-600" title={`Avanza a ${repNextStatus}`} data-testid={`advance-report-${report.id}`}>
                              <ArrowRight size={14} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(report.id, report.report_number)}
                            className="text-red-600 hover:text-red-800 p-1 h-auto"
                            title="Scarica PDF"
                          >
                            <Download size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Prezziario */}
        <TabsContent value="prezziario">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Cerca per codice o descrizione..."
                  value={priceSearch}
                  onChange={e => { setPriceSearch(e.target.value); setPricePage(1); }}
                  data-testid="price-search-input"
                />
              </div>
              <Select value={priceCategory} onValueChange={v => { setPriceCategory(v); setPricePage(1); }}>
                <SelectTrigger className="w-[160px]" data-testid="price-category-filter">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500" data-testid="price-total-count">{priceTotal} voci</span>
              {isRole('admin', 'qhse', 'capo_cantiere') && (
                <Button onClick={() => setShowPriceCreate(true)} data-testid="add-price-btn">
                  <Plus size={16} className="mr-1" /> Nuova Voce
                </Button>
              )}
            </div>
          </div>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              {priceLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <RefreshCw size={20} className="animate-spin mr-2" /> Caricamento...
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Sottocategoria</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>U.M.</TableHead>
                    <TableHead className="text-right">Fornitura</TableHead>
                    <TableHead className="text-right">Lavorazione</TableHead>
                    <TableHead className="text-right font-bold">Totale / %</TableHead>
                    {isRole('admin') && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        {priceSearch ? 'Nessun risultato per la ricerca' : 'Nessuna voce nel prezziario'}
                      </TableCell>
                    </TableRow>
                  ) : priceItems.map(item => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-100 text-slate-700 font-mono">
                          {item.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{item.description}</TableCell>
                      <TableCell className="text-sm text-slate-500">{item.subcategory || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          item.price_type === 'percentage_increase' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                          item.price_type === 'percentage_discount' ? 'bg-green-50 text-green-700 border-green-200' :
                          'bg-blue-50 text-blue-700 border-blue-200'
                        }>
                          {item.price_type === 'percentage_increase' ? 'Aumento %' :
                           item.price_type === 'percentage_discount' ? 'Sconto %' : 'Standard'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{item.price_type === 'standard' ? item.unit : '-'}</TableCell>
                      <TableCell className="text-right text-sm text-blue-700">
                        {item.price_type === 'standard' ? formatCurrency(item.price_supply) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-orange-700">
                        {item.price_type === 'standard' ? formatCurrency(item.price_work) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm font-bold">
                        {item.price_type === 'standard' 
                          ? formatCurrency(item.price_total)
                          : <span className={item.percentage_value > 0 ? 'text-orange-600' : 'text-green-600'}>
                              {item.percentage_value > 0 ? '+' : ''}{item.percentage_value}%
                            </span>
                        }
                      </TableCell>
                      {isRole('admin') && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePrice(item.id)} className="text-red-600 hover:text-red-800 p-1 h-auto">
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
              {/* Paginazione */}
              {priceTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/50">
                  <span className="text-sm text-slate-600" data-testid="price-page-info">
                    Pagina {pricePage} di {priceTotalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled={pricePage <= 1} onClick={() => setPricePage(1)} data-testid="price-page-first">
                      1
                    </Button>
                    <Button variant="outline" size="sm" disabled={pricePage <= 1} onClick={() => setPricePage(p => p - 1)} data-testid="price-page-prev">
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="px-3 py-1 text-sm font-medium text-slate-700 bg-white border rounded">{pricePage}</span>
                    <Button variant="outline" size="sm" disabled={pricePage >= priceTotalPages} onClick={() => setPricePage(p => p + 1)} data-testid="price-page-next">
                      <ChevronRight size={14} />
                    </Button>
                    <Button variant="outline" size="sm" disabled={pricePage >= priceTotalPages} onClick={() => setPricePage(priceTotalPages)} data-testid="price-page-last">
                      {priceTotalPages}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Price Dialog */}
      <Dialog open={showPriceCreate} onOpenChange={setShowPriceCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Voce Prezziario</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Codice *</Label>
              <Input value={priceForm.code} onChange={(e) => setPriceForm({ ...priceForm, code: e.target.value.toUpperCase() })} placeholder="es. SAB-001" data-testid="price-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo Voce *</Label>
              <Select value={priceForm.price_type} onValueChange={(v) => setPriceForm({ ...priceForm, price_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Descrizione *</Label>
              <Input value={priceForm.description} onChange={(e) => setPriceForm({ ...priceForm, description: e.target.value })} placeholder="es. Sabbiatura grado SA 2.5" />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={priceForm.category} onValueChange={(v) => setPriceForm({ ...priceForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sottocategoria</Label>
              <Input value={priceForm.subcategory} onChange={(e) => setPriceForm({ ...priceForm, subcategory: e.target.value })} placeholder="es. per tetti galleggianti" />
            </div>
            
            {priceForm.price_type === 'standard' ? (
              <>
                <div className="space-y-1.5">
                  <Label>Unita di Misura</Label>
                  <Select value={priceForm.unit} onValueChange={(v) => setPriceForm({ ...priceForm, unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
                <div className="space-y-1.5">
                  <Label>Prezzo Fornitura (EUR)</Label>
                  <Input type="number" step="0.01" value={priceForm.price_supply} onChange={(e) => setPriceForm({ ...priceForm, price_supply: e.target.value })} placeholder="0.00" />
                </div>
                <div className="space-y-1.5">
                  <Label>Prezzo Lavorazione (EUR)</Label>
                  <Input type="number" step="0.01" value={priceForm.price_work} onChange={(e) => setPriceForm({ ...priceForm, price_work: e.target.value })} placeholder="0.00" />
                </div>
                <div className="col-span-2 p-3 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-600">Prezzo Totale: <span className="font-bold text-slate-900">{formatCurrency((parseFloat(priceForm.price_supply) || 0) + (parseFloat(priceForm.price_work) || 0))}</span></p>
                </div>
              </>
            ) : (
              <div className="col-span-2 space-y-1.5">
                <Label>Valore Percentuale ({priceForm.price_type === 'percentage_increase' ? '+' : '-'}%) *</Label>
                <Input 
                  type="number" 
                  step="0.1" 
                  value={priceForm.percentage_value} 
                  onChange={(e) => setPriceForm({ ...priceForm, percentage_value: e.target.value })} 
                  placeholder={priceForm.price_type === 'percentage_increase' ? "es. 50 per +50%" : "es. -10 per -10%"} 
                />
                <p className="text-xs text-slate-500 mt-1">
                  {priceForm.price_type === 'percentage_increase' 
                    ? 'Inserisci un valore positivo (es. 50 per un aumento del 50%)' 
                    : 'Inserisci un valore negativo (es. -10 per uno sconto del 10%)'}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceCreate(false)}>Annulla</Button>
            <Button onClick={handleCreatePrice} disabled={!priceForm.code || !priceForm.description || (priceForm.price_type !== 'standard' && !priceForm.percentage_value)} data-testid="price-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Entry Dialog */}
      <Dialog open={showEntryCreate} onOpenChange={setShowEntryCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registra Lavoro Eseguito</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere *</Label>
              <Select value={entryForm.site_id} onValueChange={(v) => setEntryForm({ ...entryForm, site_id: v })}>
                <SelectTrigger data-testid="entry-site-select"><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
                <SelectContent>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Voce Prezziario *</Label>
              <Select value={entryForm.price_item_id} onValueChange={(v) => setEntryForm({ ...entryForm, price_item_id: v, accessory_items: [] })}>
                <SelectTrigger data-testid="entry-price-select"><SelectValue placeholder="Seleziona voce..." /></SelectTrigger>
                <SelectContent>
                  {standardItems.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} - {p.description} {p.subcategory ? `(${p.subcategory})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input type="date" value={entryForm.date} onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Quantita ({selectedPriceItem?.unit || 'u.m.'}) *</Label>
              <Input type="number" step="0.01" value={entryForm.quantity} onChange={(e) => setEntryForm({ ...entryForm, quantity: e.target.value })} placeholder="0.00" data-testid="entry-quantity" />
            </div>
            
            {/* Voci Accessorie (Variazioni %) */}
            {selectedPriceItem && entryForm.quantity && percentageItems.length > 0 && (
              <div className="col-span-2 space-y-2">
                <Label className="flex items-center gap-2">
                  Voci Accessorie (Variazioni %)
                  <Badge variant="outline" className="text-xs">{entryForm.accessory_items.length} selezionate</Badge>
                </Label>
                <div className="border rounded-lg p-3 bg-slate-50 space-y-2 max-h-40 overflow-y-auto">
                  {percentageItems.map(pItem => {
                    const isSelected = entryForm.accessory_items.some(a => a.price_item_id === pItem.id);
                    const percentageStr = pItem.percentage_value > 0 ? `+${pItem.percentage_value}%` : `${pItem.percentage_value}%`;
                    const adjustmentPreview = baseTotal * (pItem.percentage_value / 100);
                    
                    return (
                      <div 
                        key={pItem.id} 
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-blue-100 border border-blue-300' : 'bg-white hover:bg-slate-100'}`}
                        onClick={() => {
                          if (isSelected) {
                            setEntryForm({ ...entryForm, accessory_items: entryForm.accessory_items.filter(a => a.price_item_id !== pItem.id) });
                          } else {
                            setEntryForm({ ...entryForm, accessory_items: [...entryForm.accessory_items, { price_item_id: pItem.id }] });
                          }
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={isSelected} readOnly className="pointer-events-none" />
                          <div>
                            <span className="text-sm font-medium">{pItem.code}</span>
                            <span className="text-sm text-slate-600 ml-2">{pItem.description}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={pItem.percentage_value > 0 ? 'default' : 'secondary'} className={pItem.percentage_value > 0 ? 'bg-orange-500' : 'bg-green-500'}>
                            {percentageStr}
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">{formatCurrency(adjustmentPreview)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {percentageItems.length === 0 && (
                  <p className="text-sm text-slate-500 italic">Nessuna voce di variazione % disponibile. Creane una nel Prezziario.</p>
                )}
              </div>
            )}
            
            {/* Nuovi campi facoltativi */}
            <div className="space-y-1.5">
              <Label>Numero PDL</Label>
              <Input value={entryForm.numero_pdl} onChange={(e) => setEntryForm({ ...entryForm, numero_pdl: e.target.value })} placeholder="Facoltativo" />
            </div>
            <div className="space-y-1.5">
              <Label>Work Order</Label>
              <Input value={entryForm.work_order} onChange={(e) => setEntryForm({ ...entryForm, work_order: e.target.value })} placeholder="Facoltativo" />
            </div>
            <div className="space-y-1.5">
              <Label>Area</Label>
              <Input value={entryForm.area} onChange={(e) => setEntryForm({ ...entryForm, area: e.target.value })} placeholder="Facoltativo" />
            </div>
            <div className="space-y-1.5">
              <Label>Item</Label>
              <Input value={entryForm.item} onChange={(e) => setEntryForm({ ...entryForm, item: e.target.value })} placeholder="Facoltativo" />
            </div>
            
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Input value={entryForm.notes} onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} />
            </div>
            {selectedPriceItem && entryForm.quantity && (
              <div className="col-span-2 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-slate-500">Fornitura</p>
                    <p className="font-medium text-blue-700">{formatCurrency((parseFloat(entryForm.quantity) || 0) * selectedPriceItem.price_supply)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Lavorazione</p>
                    <p className="font-medium text-orange-700">{formatCurrency((parseFloat(entryForm.quantity) || 0) * selectedPriceItem.price_work)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Subtotale</p>
                    <p className="font-medium text-slate-700">{formatCurrency(baseTotal)}</p>
                  </div>
                </div>
                {entryForm.accessory_items.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs text-slate-500 mb-2">Variazioni applicate:</p>
                    {entryForm.accessory_items.map(acc => {
                      const accItem = priceItems.find(p => p.id === acc.price_item_id);
                      const adjustment = baseTotal * ((accItem?.percentage_value || 0) / 100);
                      return (
                        <div key={acc.price_item_id} className="flex justify-between text-xs">
                          <span>{accItem?.code} - {accItem?.description}</span>
                          <span className={adjustment > 0 ? 'text-orange-600' : 'text-green-600'}>
                            {adjustment > 0 ? '+' : ''}{formatCurrency(adjustment)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-green-300">
                  <div className="flex justify-between items-center">
                    <p className="text-slate-600 font-medium">TOTALE FINALE</p>
                    <p className="font-bold text-green-700 text-lg">{formatCurrency(previewTotal)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryCreate(false)}>Annulla</Button>
            <Button onClick={handleCreateEntry} disabled={!entryForm.site_id || !entryForm.price_item_id || !entryForm.quantity} data-testid="entry-save-btn">Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate SAL Dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Genera Report SAL</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cantiere *</Label>
              <Select value={generateForm.site_id} onValueChange={(v) => setGenerateForm({ ...generateForm, site_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
                <SelectContent>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Anno</Label>
                <Select value={String(generateForm.year)} onValueChange={(v) => setGenerateForm({ ...generateForm, year: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[currentYear, currentYear - 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Mese</Label>
                <Select value={String(generateForm.month)} onValueChange={(v) => setGenerateForm({ ...generateForm, month: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Input value={generateForm.notes} onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerate(false)}>Annulla</Button>
            <Button onClick={handleGenerate} disabled={!generateForm.site_id} data-testid="generate-confirm-btn">
              <Calculator size={16} className="mr-1" /> Genera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Detail Dialog */}
      <Dialog open={!!showReportDetail} onOpenChange={() => setShowReportDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {showReportDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText size={20} />
                  Report SAL: {showReportDetail.report_number}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-slate-500">Cantiere:</span> <span className="font-medium ml-1">{showReportDetail.site_name}</span></div>
                  <div><span className="text-slate-500">Cliente:</span> <span className="font-medium ml-1">{showReportDetail.client}</span></div>
                  <div><span className="text-slate-500">Periodo:</span> <span className="font-medium ml-1">{MONTHS.find(m => m.value === showReportDetail.month)?.label} {showReportDetail.year}</span></div>
                  <div><span className="text-slate-500">Stato:</span> <Badge variant="outline" className="ml-1">{showReportDetail.status}</Badge></div>
                </div>
                
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Dettaglio Lavori</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Codice</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead className="text-right">Qta</TableHead>
                          <TableHead className="text-right">Fornitura</TableHead>
                          <TableHead className="text-right">Lavorazione</TableHead>
                          <TableHead className="text-right">Totale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {showReportDetail.line_items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{item.date}</TableCell>
                            <TableCell><Badge variant="outline" className="font-mono text-xs">{item.code}</Badge></TableCell>
                            <TableCell className="text-sm">{item.description}</TableCell>
                            <TableCell className="text-right text-sm">{item.quantity} {item.unit}</TableCell>
                            <TableCell className="text-right text-sm text-blue-700">{formatCurrency(item.total_supply)}</TableCell>
                            <TableCell className="text-right text-sm text-orange-700">{formatCurrency(item.total_work)}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{formatCurrency(item.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-100 font-bold">
                          <TableCell colSpan={4}>TOTALE</TableCell>
                          <TableCell className="text-right text-blue-700">{formatCurrency(showReportDetail.total_supply)}</TableCell>
                          <TableCell className="text-right text-orange-700">{formatCurrency(showReportDetail.total_work)}</TableCell>
                          <TableCell className="text-right text-green-700">{formatCurrency(showReportDetail.total_amount)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-center text-lg">
                    <span className="text-slate-600">Totale da Fatturare:</span>
                    <span className="font-bold text-green-700 text-2xl ml-2">{formatCurrency(showReportDetail.total_amount)}</span>
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReportDetail(null)}>Chiudi</Button>
                <Button 
                  onClick={() => handleDownloadPdf(showReportDetail.id, showReportDetail.report_number)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="download-pdf-btn"
                >
                  <Download size={16} className="mr-1" /> Scarica PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
